import { useCallback, useMemo } from 'react';

import { HistoryMode, useUrlPatch } from './useUrlPatch';

interface UrlBoolOptions {
  resetPage?: boolean;
  history?: HistoryMode;
}

/**
 * Boolean param encoded as `1` / `0`. Reads back `fallback` when absent or
 * unparseable; writing the fallback deletes the param (clean URL). Works for
 * both default-false and default-true toggles.
 */
export function useUrlBool(
  key: string,
  fallback: boolean,
  options: UrlBoolOptions = {}
): [boolean, (value: boolean) => void] {
  const { resetPage = false, history = 'replace' } = options;
  const { searchParams, patch } = useUrlPatch();

  const raw = searchParams.get(key);
  const value = useMemo(() => {
    if (raw === '1') return true;
    if (raw === '0') return false;
    return fallback;
  }, [raw, fallback]);

  const setValue = useCallback(
    (next: boolean) => {
      patch((params) => {
        if (next === fallback) params.delete(key);
        else params.set(key, next ? '1' : '0');
        if (resetPage) params.delete('page');
      }, history);
    },
    [patch, key, fallback, resetPage, history]
  );

  return [value, setValue];
}
